import { Search } from "lucide-react";
import { ClientIntegrationCard } from "./client_integration_card";
import type IntegrationProvider from "@/features/integrations_provider/models/integrationProvider";
import GlassContainer from "@/shared/components/glasscontainer";

export interface ClientIntegrationsGridProps {
  providers: IntegrationProvider[];
  connectedIds: Set<string>;
  highlightedId?: string;
  loading: boolean;
  error: Error | null;
  onConnect: (providerId: string) => void;
  onDisconnect: (providerId: string) => void;
}

export function ClientIntegrationsGrid({
  providers,
  connectedIds,
  highlightedId,
  loading,
  error,
  onConnect,
  onDisconnect,
}: ClientIntegrationsGridProps) {
  if (error) {
    return (
      <GlassContainer className="p-6 mb-6 border-l-4 border-red-500 bg-red-500/5">
        <p className="text-red-700 font-medium">
          Error al cargar integraciones
        </p>
        <p className="text-red-600/80 text-sm mt-1">{error.message}</p>
      </GlassContainer>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <GlassContainer key={i} className="h-56 animate-pulse bg-white/20" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {providers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {providers.map((provider) => (
            <ClientIntegrationCard
              key={provider.id}
              provider={provider}
              isConnected={connectedIds.has(provider.id)}
              isHighlighted={highlightedId === provider.id}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
            />
          ))}
        </div>
      ) : (
        <GlassContainer className="p-12 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
            <Search className="w-8 h-8 text-accent/50" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No hay integraciones disponibles
          </h3>
          <p className="text-sm text-foreground/50 max-w-md">
            Tu administrador aún no ha configurado proveedores de integración.
          </p>
        </GlassContainer>
      )}
    </div>
  );
}
